import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import ScopePicker, { useScopeOptions } from "@/components/ScopePicker";
import { ROLE_LABEL } from "@/components/TeamUsersCard";
import { fmtMelbDay } from "@/lib/date";

const STATUSES = [
  ["current", "Currently qualified / completed"],
  ["ever", "Ever achieved"],
  ["previous", "Previously qualified, not current"],
];
const DATE_LABEL = { current: "Qualified since", ever: "First achieved", previous: "Ceased" };

export default function MilestoneReport() {
  const { options, loading } = useScopeOptions();
  const [catalog, setCatalog] = useState(null);
  const [f, setF] = useState({ scope: "team", milestone_id: "rising_star_3x6", status: "current", date_from: "", date_to: "", period: "",
    q: "", team: "", dcr_min: "1", launch_from: "", launch_to: "" });
  const [res, setRes] = useState(null);
  const [summary, setSummary] = useState(null);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));

  useEffect(() => { api.get("/milestones/catalog").then((r) => setCatalog(r.data)); }, []);
  useEffect(() => {
    if (loading || options.some((o) => o.value === f.scope)) return;
    const pick = ["team", "all", "personal"].find((v) => options.some((o) => o.value === v));
    setF((o) => ({ ...o, scope: pick }));
  }, [options, loading, f.scope]);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      api.get("/milestones/report", { params }).then((r) => setRes(r.data)).catch((e) => setRes({ count: 0, people: [], denied: e?.response?.status === 403 }));
      const { scope, q, team, launch_from, launch_to } = params;
      api.get("/milestones/summary", { params: { scope, q, team, launch_from, launch_to } }).then((r) => setSummary(r.data)).catch(() => setSummary(null));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);

  const isBootcamp = f.milestone_id === "bootcamp_45_days";
  const isCounter = f.milestone_id === "core_run_90";
  const annual = catalog?.groups.flatMap((g) => g.items).find((i) => i.id === f.milestone_id)?.annual;
  const years = catalog ? Array.from({ length: 12 }, (_, i) => String(catalog.current_year + 1 - i)) : [];

  if (res?.denied) return <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="report-denied">Milestone reports are for leaders and admins.</div>;

  return (
    <div className="space-y-6" data-testid="milestone-report-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Reports</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">Milestones & qualifications</h1>
        <p className="text-sm text-slate-500 mt-1">Counts and people within the branches you're allowed to see. Dates are Melbourne time.</p>
      </div>
      <Card className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div><Label className="text-xs">View</Label><ScopePicker value={f.scope} onChange={set("scope")} options={options} testid="report-scope" /></div>
        <div>
          <Label className="text-xs">Milestone or qualification</Label>
          <Select value={f.milestone_id} onValueChange={(v) => setF((o) => ({ ...o, milestone_id: v, period: "" }))}>
            <SelectTrigger className="h-9" data-testid="report-milestone"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80">
              {catalog?.groups.map((g) => (
                <SelectGroup key={g.id}><SelectLabel>{g.title}</SelectLabel>
                  {g.items.map((i) => <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>)}
                </SelectGroup>
              ))}
              <SelectGroup><SelectLabel>Bootcamp data</SelectLabel><SelectItem value="bootcamp_45_days">All 45 Bootcamp days ticked</SelectItem></SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {!isBootcamp && !isCounter && (
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger className="h-9" data-testid="report-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {annual && (
          <div>
            <Label className="text-xs">Qualification year</Label>
            <Select value={f.period || "any"} onValueChange={(v) => set("period")(v === "any" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="report-period"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any year</SelectItem>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {isCounter && <div><Label className="text-xs">Completed at least</Label><Input type="number" min="1" className="h-9" value={f.dcr_min} onChange={(e) => set("dcr_min")(e.target.value)} data-testid="report-dcr-min" /></div>}
        {!isCounter && <div><Label className="text-xs">{isBootcamp ? "Finished" : DATE_LABEL[f.status]} from</Label><Input type="date" className="h-9" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="report-date-from" /></div>}
        {!isCounter && <div><Label className="text-xs">to</Label><Input type="date" className="h-9" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="report-date-to" /></div>}
        <div><Label className="text-xs">Name or email</Label><Input className="h-9" value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search" data-testid="report-q" /></div>
        <div><Label className="text-xs">Team</Label><Input className="h-9" value={f.team} onChange={(e) => set("team")(e.target.value)} placeholder="e.g. Team Aho" data-testid="report-team" /></div>
        <div><Label className="text-xs">Launched into partnership from</Label><Input type="date" className="h-9" value={f.launch_from} onChange={(e) => set("launch_from")(e.target.value)} data-testid="report-launch-from" /></div>
        <div><Label className="text-xs">to</Label><Input type="date" className="h-9" value={f.launch_to} onChange={(e) => set("launch_to")(e.target.value)} data-testid="report-launch-to" /></div>
      </Card>
      {summary && catalog && (
        <Card className="p-4" data-testid="report-summary">
          <div className="text-xs text-slate-500 mb-2">Counts within this view ({summary.people} people). Tap one to see who.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {catalog.groups.flatMap((g) => g.items.map((i) => ({ ...i, kind: g.kind }))).map((i) => {
              const c = summary.counts[i.id] || {};
              return (
                <button key={i.id} type="button" onClick={() => setF((o) => ({ ...o, milestone_id: i.id, status: "current", period: "" }))}
                        className={`text-left rounded-lg border px-3 py-2 min-h-[44px] ${f.milestone_id === i.id ? "border-[#C9A961] bg-[#C9A961]/10" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`report-summary-${i.id}`}>
                  <div className="text-xs font-medium text-slate-800 break-words">{i.title}</div>
                  <div className="text-[11px] text-slate-500">{i.kind === "counter" ? `${c.current || 0} people · ${c.total || 0} runs in total` : i.kind === "qualification" ? `${c.current || 0} current · ${c.ever || 0} ever` : `${c.ever || 0} achieved`}</div>
                </button>
              );
            })}
          </div>
        </Card>
      )}
      <Card className="p-5">
        <div className="flex items-baseline gap-2 mb-3">
          <span className="font-serif text-4xl text-[#0A1F44]" data-testid="report-count">{res ? res.count : "…"}</span>
          <span className="text-sm text-slate-500">{res?.count === 1 ? "person" : "people"} · all entries are self-reported unless marked otherwise</span>
        </div>
        <div className="divide-y divide-slate-100" data-testid="report-list">
          {res?.people.map((p) => (
            <Link key={p.id} to={`/people/${p.id}`} className="flex items-center justify-between gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`report-person-${p.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 break-words">{p.name}</div>
                <div className="text-[11px] text-slate-500">{ROLE_LABEL[p.role] || p.role} · {p.upline_team || "No team"}{p.periods?.length ? ` · Years: ${p.periods.join(", ")}` : ""}
                  {p.partnership_launch_date ? ` · launched ${fmtMelbDay(p.partnership_launch_date)}` : ""}
                  {p.self_reported === false && <span className="ml-1 text-[#0A1F44] font-medium" data-testid={`report-admin-recorded-${p.id}`}>· recorded by Admin</span>}</div>
              </div>
              <span className="text-xs text-slate-600 shrink-0">{isCounter ? `${p.count} DCR` : p.date ? fmtMelbDay(p.date) : p.first_date_unknown ? "date unknown" : "—"}</span>
            </Link>
          ))}
          {res && !res.people.length && <div className="text-sm text-slate-500 py-3" data-testid="report-empty">No one matches these filters.</div>}
        </div>
      </Card>
    </div>
  );
}
